import React, { useEffect, useState } from 'react'
import { useBreadcrumbs } from '../../../../core/contexts/BreadCrumbsContext';
import { Backdrop, Box, Card, CardContent, CardHeader, CircularProgress, Divider, Grid, Tab, Tabs } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { AppointmentDashboardDTO, PatientHistoryItemDTO, ReceiptDashboardDTO, ShoppingCartDashboardDTO } from '../../../../api/DashboardWidgetDTO';
import { DashboardWidgetsClient } from '../../../../api/DashboardWidgetsClient';
import { REACT_APP_PORTAL_API_URL } from '../../../../KeyVariables';
import { AnalyzisArchive } from './Archives/AnalyzisArchive';
import { PaymentsArchive } from './Archives/PaymentsArchive';
import { AppointmentsArchive } from './Archives/AppointmentsArchive';
import { DICOMArchive } from './Archives/DICOMArchive';
import { InvestigationsArchive } from './Archives/InvestigationsArchive';
import { ConsultsArchive } from './Archives/ConsultsArchive';
import { ShoppingCartsArchive } from './Archives/ShoppingCartsArchive';
import { useTranslation } from 'react-i18next';

export interface PatientArchiveProps {
  patientId: number;
}

function PatientArchive() {
  const breadcrumbsContext = useBreadcrumbs();
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get("PatientId") ? Number(searchParams.get("PatientId")) : 0;
  const [tab, setTab] = useState(searchParams.get("Tab") || 'consults');
  const [historyItems, setHistoryItems] = useState<PatientHistoryItemDTO[]>([]);
  const [appointments, setAppointments] = useState<AppointmentDashboardDTO[]>([]);
  const { t } = useTranslation("Portal");
  
  const [payments, setPayments] = useState<ReceiptDashboardDTO[]>([]);
  const [shoppingCarts, setShoppingCarts] = useState<ShoppingCartDashboardDTO[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const client = new DashboardWidgetsClient(REACT_APP_PORTAL_API_URL);

      const requests = [
        client.history(patientId > 0 ? patientId : undefined),
        client.appointments(patientId > 0 ? patientId : undefined),
        client.payments(patientId > 0 ? patientId : undefined),
        client.shoppingcarts(patientId > 0 ? patientId : undefined),
      ];

      const [historyResult, appointmentsResult, paymentsResult, shoppingcartResult] = await Promise.allSettled(requests);

      if (historyResult.status === 'fulfilled') {
        setHistoryItems(historyResult.value);
      } else {
        console.log('Ошибка при получении истории:', historyResult.reason);
      }

      if (appointmentsResult.status === 'fulfilled') {
        setAppointments(appointmentsResult.value.sort((a, b) => b.date - a.date));
      } else {
        console.log('Ошибка при получении записей:', appointmentsResult.reason);
      }

      if (paymentsResult.status === 'fulfilled') {
        setPayments(paymentsResult.value.filter(x => x.price! > 0));
      } else {
        console.log('Ошибка при получении платежей:', paymentsResult.reason);
      }

      if (shoppingcartResult.status === 'fulfilled') {
        setShoppingCarts(shoppingcartResult.value.sort((a, b) => b.date - a.date));
      } else {
        console.log('Ошибка при получении записей:', shoppingcartResult.reason);
      }


      setIsLoading(false);
    };


    const breadcrumbs = [
      { breadcrumb: 'Dashboard', path: '/' },
    ];
    breadcrumbsContext.updateBreadcrumbs(breadcrumbs);

    fetchData();
  }, [])

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
  };

  return (
    <>
      <Helmet>
        <title>Arhiva</title>
      </Helmet>
      <Grid container justifyContent="center" sx={{ mt: 2 }}>
        <Card sx={{ width: '50vw', minHeight: '80vh', p: 2 }}>
          <Tabs value={tab} onChange={handleTabChange} indicatorColor="primary" textColor="primary" >
            <Tab label={t("Portal_ArchiveConsults")} value="consults" />
            <Tab label={t("Portal_ArchiveInvest")} value="investigation" />
            <Tab label={t("Portal_ArchiveAnalysis")} value="analyzis" />
            <Tab label={t("Portal_ArchiveImag")} value="dicom" />
            <Tab label={t("Portal_Appointments")} value="appointments" />
            <Tab label={t("Portal_ReceiptsList")} value="payments" />
            <Tab label={t("Portal_Carts")} value="shoppingcarts" />
          </Tabs>
          <Divider sx={{ mt: 1 }} />
          <Box sx={{ mt: 1 }}>
            {isLoading ? (
              <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isLoading}
              >
                <CircularProgress color="inherit" />
              </Backdrop>
            ) : (
              <>
                {tab === 'consults' && <ConsultsArchive items={historyItems.filter(x => x.type == "consultation")} />}
                {tab === 'investigation' && <InvestigationsArchive items={historyItems.filter(x => x.type == "investigation")} />}
                {tab === 'analyzis' && <AnalyzisArchive items={historyItems.filter(x => x.type == "analyzis")} />}
                {tab === 'dicom' && <DICOMArchive items={historyItems.filter(x => x.type == "dicom" || (x.images && x.images?.length > 0))} />}
                {tab === 'appointments' && <AppointmentsArchive items={appointments} />}
                {tab === 'payments' && <PaymentsArchive items={payments} />}
                {tab === 'shoppingcarts' && <ShoppingCartsArchive items={shoppingCarts} />}
              </>
            )}
          </Box>
        </Card>
      </Grid>
    </>
  );
}

export default PatientArchive